import { Bom, BomAdd, BomAddItem, BomAddReplaceItem } from "@/types/bom";
import { Button, DatePicker, Flex, Form, Input, Modal, Select, Spin, Table, TableColumnsType, TableProps } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { addBom } from "@/apis/bom";
import SText from "@/styles/SText";
import { styled } from "styled-components";

const TableWrapper = styled.div`
  width: 100%;
  height: 100%;

  /* 隐藏滚动条 */
  .ant-table-body::-webkit-scrollbar {
    display: none;
  }
  .ant-table-body {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

interface BomAddFormProps {
  data: BomAdd;
  onClose: () => void;
  onSucc: (bom: Bom) => void;
};

const BomAddForm: FC<BomAddFormProps> = ({ data, onClose, onSucc }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<BomAdd>();
  const [expandIds, setExpandIds] = useState<Array<string>>([]);
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number>(300)

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef?.current?.clientHeight - 40)
    }
  }, [tableRef.current])
  const handleOK = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const result = await addBom({
        ...values,
        items: [...data.items]
      });
      onSucc(result);
    } catch (error) {
      console.error("Add BOM error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 替代料表格列配置
  const getRepsColumns = (): TableColumnsType<BomAddReplaceItem> => [
    {
      title: t('bom.itemReq'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('purchase.goodName'),
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: t('sale.prodMod'),
      dataIndex: 'model',
      key: 'model',
      width: 120,
      render: (_, record) => <SText text={record.model} />
    },
    {
      title: t('bom.itemUnit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
    },
    {
      title: t('bom.itemBase'),
      dataIndex: 'baseUnit',
      key: 'baseUnit',
      width: 80,
    },
    {
      title: t('bom.itemBaseQuantity'),
      dataIndex: 'baseQuantity',
      key: 'baseQuantity',
      width: 100,
    },
    {
      title: t('bom.itemAttritionRate'),
      dataIndex: 'attritionRate',
      key: 'attritionRate',
      width: 100,
    },
    {
      title: t('bom.itemUseQuantity'),
      dataIndex: 'useQuantity',
      key: 'useQuantity',
      width: 100,
    },
    {
      title: t('bom.itemExtraRate'),
      dataIndex: 'extraRate',
      key: 'extraRate',
      width: 80,
      render: (val) => val ? `${val}%` : '',
    },
    {
      title: t('bom.itemEffectiveDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
    },
    {
      title: t('bom.itemExpiringDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
    },
    {
      title: t('bom.itemReMark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
    },
    {
      title: t('bom.itemLocators'),
      dataIndex: 'locators',
      key: 'locators',
      width: 120,
      render: (_, record) => <SText text={record.locators} />

    },
  ];

  // 主表格列配置
  const columns: TableColumnsType<BomAddItem> = [
    {
      title: t('bom.itemLevel'),
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (info) => t('bom.itemLevelInfo', { level: info || 0 }),
    },
    {
      title: t('bom.itemCode'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('bom.itemName'),
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: t('bom.itemModal'),
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (_, record) => <SText text={record.model} />
    },
    {
      title: t('bom.itemUnit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
    },
    {
      title: t('bom.itemBase'),
      dataIndex: 'baseUnit',
      key: 'baseUnit',
      width: 80,
    },
    {
      title: t('bom.itemBaseQuantity'),
      dataIndex: 'baseQuantity',
      key: 'baseQuantity',
      width: 100,
    },
    {
      title: t('product.process'),
      dataIndex: 'process',
      key: 'process',
      width: 100,
    },
    {
      title: t('bom.itemAttritionRate'),
      dataIndex: 'attritionRate',
      key: 'attritionRate',
      width: 100,
    },
    {
      title: t('bom.itemUseQuantity'),
      dataIndex: 'useQuantity',
      key: 'useQuantity',
      width: 100,
    },
    {
      title: t('bom.itemExtraRate'),
      dataIndex: 'extraRate',
      key: 'extraRate',
      width: 80,
      render: (val) => val ? `${val}%` : '',
    },
    {
      title: t('bom.itemEffectiveDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
    },
    {
      title: t('bom.itemExpiringDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
    },
    {
      title: t('bom.itemReMark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
    },
    {
      title: t('bom.itemLocators'),
      dataIndex: 'locators',
      key: 'locators',
      width: 120,
      render: (_, record) => <SText text={record.locators} />
    },
  ];

  // 可展开行的渲染函数（渲染 reps）
  const expandedRowRender = (record: BomAddItem) => (
    <Table<BomAddReplaceItem>
      columns={getRepsColumns()}
      dataSource={record.reps || []}
      rowKey={(item, index) => `${record.code}_rep_${index}`}
      pagination={false}
      size="small"
      scroll={{ x: 1000, y: tableHeight }}
      bordered
    />
  );

  const tableProps: TableProps<BomAddItem> = {
    bordered: true,
    rowKey: (record, index) => `${record.code}_${index}`,
    expandable: {
      expandedRowRender,
      rowExpandable: (record) => record.reps && record.reps.length > 0,
      expandedRowKeys: expandIds,
      onExpand: (expanded, record) => {
        const key = `${record.code}_${formData.items?.indexOf(record)}`;
        if (expanded) {
          setExpandIds([...expandIds, key]);
        } else {
          setExpandIds(prev => prev.filter(item => item !== key));
        }
      },
    },
    pagination: false,
    size: "small",
    scroll: { x: 1000, y: tableHeight },
  };

  const formData = useMemo(() => data, [data]);

  const allExpandableIds = useMemo(() => {
    const ids: string[] = [];
    formData.items?.forEach((item, index) => {
      if (item.reps && item.reps.length > 0) {
        ids.push(`${item.code}_${index}`);
      }
    });
    return ids;
  }, [formData.items]);

  const handleExpandAll = () => {
    setExpandIds(allExpandableIds);
  };

  const handleCollapseAll = () => {
    setExpandIds([]);
  };

  return (
    <Modal
      open
      title={t('bom.addBom')}
      width={1200}
      maskClosable={false}
      centered
      onCancel={onClose}
      onOk={handleOK}
      confirmLoading={loading}
    >
      <Spin spinning={loading}>
        <Flex vertical gap={12} style={{ height: "70vh" }}>
          {/* 上半部分：Form 组件 */}
          <Form
            form={form}
            initialValues={formData}
          >
            <Flex gap={12}>
              <Form.Item label={t('bom.bomCode')} name="code" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomName')} name="name" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomModal')} name="model" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomUnit')} name="unit" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </Flex>
            <Flex gap={12}>
              <Form.Item label={t('bom.bomVersion')} name="version" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomVersionDesc')} name="versionDesc" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomStatus')} name="status" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.approvaler')} name="approvaler" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </Flex>
            <Flex gap={12}>
              <Form.Item
                label={t('bom.bomVersionDate')}
                name="bomDate"
                style={{ flex: 1 }}
                getValueProps={(value) => ({ value: value && dayjs(Number(value)) })}
                normalize={(value) => value && dayjs(value).valueOf()}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label={t('bom.createDate')} name="bomCreateDate"
                getValueProps={(value) => ({ value: value && dayjs(Number(value)) })}
                normalize={(value) => value && dayjs(value).valueOf()} style={{ flex: 1 }}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label={t('bom.creator')} name="bomCreateDateBy" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </Flex>
            <Flex gap={12}>
              <Form.Item
                label={t('bom.updateDate')}
                name="bomUpdateDate"
                style={{ flex: 1 }}
                getValueProps={(value) => ({ value: value && dayjs(Number(value)) })}
                normalize={(value) => value && dayjs(value).valueOf()}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label={t('bom.updater')} name="bomUpdateDateBy" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
              <Form.Item label={t('bom.bomType')} name="type" style={{ flex: 1 }}>
                <Input />
              </Form.Item>
            </Flex>
          </Form>

          {/* 下半部分：可展开表格 */}
          <Flex vertical gap={8} style={{ flex: 1, overflow: "hidden" }}>
            <Flex gap={8}>
              <Button size="small" onClick={handleExpandAll}>{t('bom.expandAll')}</Button>
              <Button size="small" onClick={handleCollapseAll}>{t('bom.collapseAll')}</Button>
            </Flex>
            <TableWrapper ref={tableRef}>
              <Table<BomAddItem>
                columns={columns}
                dataSource={formData.items || []}
                {...tableProps}
                style={{ flex: 1 }}
              />
            </TableWrapper>
          </Flex>
        </Flex>
      </Spin>
    </Modal>
  );
};

export default BomAddForm;
